import React, { useState, useEffect, useRef } from "react";
import styles from "./ClassroomLesson.module.less";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  getLessonChatHistory,
  getLessonScore,
  getLessonTime,
  getPhonetic,
  unwordCollections,
  wordCollections,
} from "@/api/lesson";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Toast } from "react-vant";
import { Star } from "@react-vant/icons";
import CommonVideo from "views/components/CommonVideo";

const parseBinData = (binData) => {
  try {
    return JSON.parse(binData);
  } catch {
    return [];
  }
};

const processSSEString = (sseString) => {
  const events = sseString.split("\n\n");
  let extractedText = "";

  for (const event of events) {
    if (event.trim()) {
      const lines = event.split("\n");
      let data = null;

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          data = line.slice(6);
          break;
        }
      }

      if (data) {
        try {
          const jsonData = JSON.parse(data);
          const text = jsonData.text || "";
          extractedText += text;
        } catch (parseError) {
          console.warn("JSON 解析失败:", parseError, "数据:", data);
        }
      }
    }
  }

  return extractedText;
};

const ClassroomLesson = () => {
  const navigate = useNavigate();
  const { lessonId: paramLessonId } = useParams();
  const [searchParams] = useSearchParams();
  const lessonId = paramLessonId || searchParams.get("lessonId") || "31930";
  const urlToken = searchParams.get("token");
  const hideTopBar = searchParams.get("tab") === "false";
  const [info, setInfo] = useState(null);
  const [chatData, setChatData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const chatAreaRef = useRef(null);
  const swiperRef = useRef(null);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState("");
  const [audioState, setAudioState] = useState({
    srcs: [],
    playing: false,
    index: 0,
  });
  const [queryVisible, setQueryVisible] = useState(false);
  const [queryData, setQueryData] = useState(null);
  const [queryText, setQueryText] = useState("");
  const [starShow, setStarShow] = useState(false);
  const [starLoading, setStarLoading] = useState(false);
  const globalAudioRef = useRef(null);
  const currentRequestRef = useRef(0);
  const [isLandscape, setIsLandscape] = useState(false);
  const [highlightedWord, setHighlightedWord] = useState({
    rowId: null,
    index: null,
  });

  // 获取CDN域名
  const cdnDomain = process.env.REACT_APP_API_CDN || "";

  // 提取所有图片/视频消息，生成课件区数据
  const mediaList = [];
  chatData.forEach((item, idx) => {
    if (item.type === "image") {
      parseBinData(item.bin_data).forEach((img) => {
        mediaList.push({
          id: item.id,
          type: "image",
          src: img.startsWith("http") ? img : `${cdnDomain}/${img}`,
          chatIdx: idx,
        });
      });
    }
    if (item.type === "video") {
      parseBinData(item.bin_data).forEach((video) => {
        mediaList.push({
          id: item.id,
          type: "video",
          src: video.startsWith("http") ? video : `${cdnDomain}/${video}`,
          chatIdx: idx,
        });
      });
    }
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getLessonScore(lessonId, urlToken),
      getLessonTime(lessonId, urlToken),
      getLessonChatHistory(lessonId, urlToken),
    ])
      .then(([scoreRes, timeRes, chatRes]) => {
        if (chatRes.code === 200 && chatRes.status && chatRes.data) {
          setInfo(chatRes.data.info);
          const dataWithIndex = chatRes.data.data.map((item, index) => {
            return {
              ...item,
              index,
            };
          });
          setChatData(dataWithIndex || []);
        }
        if (scoreRes.code === 200 && scoreRes.status && scoreRes.data) {
          setScore(scoreRes.data.score);
        }
        if (timeRes.code === 200 && timeRes.status && timeRes.data) {
          setTime(timeRes.data.time);
        }
      })
      .finally(() => setLoading(false));
  }, [lessonId, urlToken]);

  // 只保留文本消息
  const textChatData = chatData.filter(
    (item) =>
      item.type === "teacher" || item.type === "user" || item.type === "audio",
  );

  // 聊天区滚动时，自动切换轮播到当前可见的图片/视频
  const handleChatScroll = () => {
    if (!chatAreaRef.current || mediaList.length === 0) return;
    const chatAreaRect = chatAreaRef.current.getBoundingClientRect();
    const chatItems = chatAreaRef.current.querySelectorAll(
      "." + styles.chatItem,
    );

    // 精准联动：找到第一个进入可视区的chatItem的data-id，匹配mediaList
    let foundIdx = slideIndex; // 默认保持当前页
    for (let i = 0; i < chatItems.length; i++) {
      const dom = chatItems[i];
      const rect = dom.getBoundingClientRect();
      const relativeTop = rect.top - chatAreaRect.top;
      if (relativeTop >= 0) {
        const id = dom.getAttribute("data-id");
        const mediaIdx = mediaList.map((m) => String(m.id)).lastIndexOf(id);
        if (mediaIdx !== -1) {
          foundIdx = mediaIdx;
        }
        break;
      }
    }
    // 判断最后一条图片/视频消息是否完全可见
    if (mediaList.length > 0) {
      const lastMedia = mediaList[mediaList.length - 1];
      const lastDom = chatItems[lastMedia.chatIdx];
      if (lastDom) {
        const lastRect = lastDom.getBoundingClientRect();
        if (lastRect.bottom <= chatAreaRect.bottom) {
          foundIdx = mediaList.length - 1;
        }
      }
    }
    if (foundIdx !== slideIndex) {
      setSlideIndex(foundIdx);
      if (swiperRef.current && swiperRef.current.slideTo) {
        swiperRef.current.slideTo(foundIdx);
      }
    }
  };

  // 播放队列
  const handlePlayAudios = (srcs) => {
    if (!srcs || srcs.length === 0) return;
    // 如果当前已在播放且srcs相同，则暂停
    if (
      audioState.playing &&
      JSON.stringify(audioState.srcs) === JSON.stringify(srcs)
    ) {
      globalAudioRef.current.pause();
      return;
    }
    setAudioState({ srcs, playing: true, index: 0 });
  };

  // 切换音频时自动播放下一个
  useEffect(() => {
    if (!audioState.playing || !audioState.srcs.length) return;
    if (!globalAudioRef.current) return;
    globalAudioRef.current.src = audioState.srcs[audioState.index];
    globalAudioRef.current.play();
  }, [audioState.index, audioState.playing, audioState.srcs]);

  // 监听audio事件
  useEffect(() => {
    const audio = globalAudioRef.current;
    if (!audio) return;
    const handleEnded = () => {
      if (audioState.index < audioState.srcs.length - 1) {
        setAudioState((s) => ({ ...s, index: s.index + 1, playing: true }));
      } else {
        setAudioState((s) => ({ ...s, playing: false, index: 0 }));
      }
    };
    const handlePause = () => {
      setAudioState((s) => ({ ...s, playing: false }));
    };
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);
    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
    };
  }, [audioState]);

  async function simpleTextStream(data, onProgress, onComplete, requestUrl) {
    const token = localStorage.getItem("access_token");
    let reader = null;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${requestUrl}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : undefined,
          },
          body: JSON.stringify(data),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let rawResult = ""; // 保存原始 SSE 字符串

      while (true) {
        try {
          const { done, value } = await reader.read();

          if (done) {
            // 在这里调用完成回调
            onComplete && onComplete();
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          rawResult += chunk; // 累积原始字符串

          // 使用处理函数提取文本
          const processedText = processSSEString(rawResult);

          // 实时回调更新
          onProgress && onProgress(chunk, processedText);
        } catch (readError) {
          console.error("读取流数据时出错:", readError);

          // 根据错误类型提供不同的处理
          if (
            readError.name === "TypeError" &&
            readError.message.includes("fetch")
          ) {
            throw new Error("网络连接中断，请检查网络连接");
          } else if (readError.name === "NetworkError") {
            throw new Error("网络错误，请重试");
          } else {
            throw new Error("读取数据失败: " + readError.message);
          }
        }
      }

      // 最终处理完整的 SSE 字符串
      const finalText = processSSEString(rawResult);
      return finalText;
    } catch (error) {
      console.error("流式请求失败:", error);
      throw error;
    } finally {
      // 清理资源
      if (reader) {
        try {
          reader.cancel();
          reader.releaseLock();
        } catch (cleanupError) {
          console.warn("清理reader时出错:", cleanupError);
        }
      }
    }
  }
  //语句优化
  const handleOptimize = async (item) => {
    if (item.optimizeShow === true) {
      setChatData((prev) =>
        prev.map((i) =>
          i.index === item.index ? { ...i, optimizeShow: false } : i,
        ),
      );
      return;
    } else {
      setChatData((prev) =>
        prev.map((i) =>
          i.index === item.index ? { ...i, optimizeShow: true } : i,
        ),
      );
      if (item.optimizeStatus === "done") {
        return;
      }
    }

    const findIndex = textChatData.findIndex((i) => i.index === item.index);
    const params = {
      context: [],
    };
    if (textChatData[findIndex - 1]) {
      params.context.push(textChatData[findIndex - 1].message);
    } else {
      params.context.push("");
    }
    params.context.push(item.message);
    params.context.push(item.answer);

    try {
      await simpleTextStream(
        params,
        (chunk, fullResult) => {
          setChatData((prev) =>
            prev.map((i) =>
              i.index === item.index
                ? {
                    ...i,
                    optimizeStatus: "loading",
                    optimizeText: fullResult,
                  }
                : i,
            ),
          );
        },
        () => {
          setChatData((prev) =>
            prev.map((i) =>
              i.index === item.index
                ? {
                    ...i,
                    optimizeStatus: "done",
                  }
                : i,
            ),
          );
        },
        "/ecdict/sentenceOptimizev2.php",
      );
    } catch (error) {
      setChatData((prev) =>
        prev.map((i) =>
          i.index === item.index
            ? {
                ...i,
                optimizeStatus: "error",
                optimizeText: "语句优化失败，请重试...",
              }
            : i,
        ),
      );

      if (error.message.includes("网络连接中断")) {
        Toast.fail("网络连接中断，请检查网络后重试");
      } else if (error.message.includes("网络错误")) {
        Toast.fail("网络不稳定，请重试");
      } else {
        Toast.fail("翻译失败: " + error.message);
      }
    }
  };
  //过滤中文以及中文后面的内容
  const filterChineseAndAfter = (text) => {
    if (!text || typeof text !== "string") {
      return text;
    }

    // 找到第一个中文字符的位置
    const chineseRegex = /[\u4e00-\u9fff]/;
    const match = text.match(chineseRegex);

    if (match) {
      // 如果找到中文，截取中文之前的部分
      return text.substring(0, match.index).trim();
    }

    // 如果没有中文，返回原文本
    return text;
  };

  //智能助答
  const handleHelp = async (item) => {
    if (item.helpShow === true) {
      setChatData((prev) =>
        prev.map((i) =>
          i.index === item.index ? { ...i, helpShow: false } : i,
        ),
      );
      return;
    } else {
      setChatData((prev) =>
        prev.map((i) =>
          i.index === item.index ? { ...i, helpShow: true } : i,
        ),
      );
      if (item.helpStatus === "done") {
        return;
      }
    }

    const findIndex = textChatData.findIndex((i) => i.index === item.index);
    const params = {
      context: filterChineseAndAfter(item.message),
      text: [],
    };
    for (let i = 3; i > 0; i--) {
      if (findIndex - i >= 0) {
        params.text.push(
          filterChineseAndAfter(textChatData[findIndex - i].message),
        );
      }
    }

    try {
      await simpleTextStream(
        params,
        (chunk, fullResult) => {
          setChatData((prev) =>
            prev.map((i) =>
              i.index === item.index
                ? {
                    ...i,
                    helpStatus: "loading",
                    helpText: fullResult,
                  }
                : i,
            ),
          );
        },
        () => {
          setChatData((prev) =>
            prev.map((i) =>
              i.index === item.index
                ? {
                    ...i,
                    helpStatus: "done",
                  }
                : i,
            ),
          );
        },
        "/ecdict/IntelligentAssistantV3.php",
      );
    } catch (error) {
      setChatData((prev) =>
        prev.map((i) =>
          i.index === item.index
            ? {
                ...i,
                helpStatus: "error",
                helpText: "智能助答失败，请重试...",
              }
            : i,
        ),
      );

      if (error.message.includes("网络连接中断")) {
        Toast.fail("网络连接中断，请检查网络后重试");
      } else if (error.message.includes("网络错误")) {
        Toast.fail("网络不稳定，请重试");
      } else {
        Toast.fail("翻译失败: " + error.message);
      }
    }
  };

  //翻译
  const handleTranslate = async (item) => {
    if (item.translationShow === true) {
      setChatData((prev) =>
        prev.map((i) =>
          i.index === item.index ? { ...i, translationShow: false } : i,
        ),
      );
      return;
    } else {
      setChatData((prev) =>
        prev.map((i) =>
          i.index === item.index ? { ...i, translationShow: true } : i,
        ),
      );
      if (item.translationStatus === "done") {
        return;
      }
    }

    const findIndex = textChatData.findIndex((i) => i.index === item.index);
    const params = {
      context: [],
      text: item.message,
    };
    for (let i = 3; i > 0; i--) {
      if (findIndex - i >= 0) {
        params.context.push(textChatData[findIndex - i].message);
      }
    }

    try {
      await simpleTextStream(
        params,
        (chunk, fullResult) => {
          setChatData((prev) =>
            prev.map((i) =>
              i.index === item.index
                ? {
                    ...i,
                    translationStatus: "loading",
                    translationText: fullResult,
                  }
                : i,
            ),
          );
        },
        () => {
          setChatData((prev) =>
            prev.map((i) =>
              i.index === item.index
                ? {
                    ...i,
                    translationStatus: "done",
                  }
                : i,
            ),
          );
        },
        "/assistant/translateV2.php",
      );
    } catch (error) {
      setChatData((prev) =>
        prev.map((i) =>
          i.index === item.index
            ? {
                ...i,
                translationStatus: "error",
                translationText: "翻译失败，请重试...",
              }
            : i,
        ),
      );
      if (error.message.includes("网络连接中断")) {
        Toast.fail("网络连接中断，请检查网络后重试");
      } else if (error.message.includes("网络错误")) {
        Toast.fail("网络不稳定，请重试");
      } else {
        Toast.fail("翻译失败: " + error.message);
      }
    }
  };

  //转换span - 简洁版本
  const convertSpan = (text, onWordClick, rowId) => {
    if (!text || typeof text !== "string") {
      return text;
    }

    // 分割文本，保留分隔符
    const parts = text.split(/(\s+|[,.!?;:()[\]{}""''—–-])/);

    return parts.map((part, index) => {
      // 空白字符直接返回
      if (/^\s+$/.test(part)) {
        return part;
      }

      // 检查是否为标点符号
      const isPunctuation = /^[,.!?;:()[\]{}""''—–-]$/.test(part);

      return (
        <span
          key={index}
          className={`${styles.word} ${
            highlightedWord.rowId === rowId && highlightedWord.index === index
              ? styles.wordHighlighted
              : ""
          }`}
          onClick={() => {
            // 非标点符号且非空才触发点击
            if (!isPunctuation && part.trim() && onWordClick) {
              onWordClick(part.trim(), index);
              setHighlightedWord({
                rowId: rowId,
                index: index,
              });
            }
          }}
        >
          {part}
        </span>
      );
    });
  };

  // 获取单词信息
  const getWordInfo = async (word) => {
    const requestId = ++currentRequestRef.current;
    try {
      const res = await getPhonetic({
        text: word,
        member_id: JSON.parse(localStorage.getItem("userInfo")).member_id,
        token: localStorage.getItem("access_token"),
      });

      if (requestId === currentRequestRef.current) {
        if (res.code === 200) {
          setQueryData({
            ...res.data,
            word,
          });
        } else {
          Toast.fail("获取单词信息失败，请重试...");
        }
      }
    } catch {
      if (requestId === currentRequestRef.current) {
        Toast.fail("单词信息处理失败，请重试...");
      }
    }
    try {
      await simpleTextStream(
        { text: word, token: localStorage.getItem("access_token") },
        (chunk, fullResult) => {
          if (requestId === currentRequestRef.current) {
            setQueryText(fullResult);
          }
        },
        () => {
          setStarShow(true);
        },
        "/ecdictv2/word.php",
      );
    } catch (error) {
      if (requestId === currentRequestRef.current) {
        if (error.message.includes("网络连接中断")) {
          Toast.fail("网络连接中断，请检查网络后重试");
        } else if (error.message.includes("网络错误")) {
          Toast.fail("网络不稳定，请重试");
        } else {
          Toast.fail("获取单词信息失败: " + error.message);
        }
      }
    }
  };
  // 处理单词点击
  const handleWordClick = (word, index) => {
    console.log("点击了单词:", word, "索引:", index);
    // 直接使用正则表达式判断中文
    const isChinese = /[\u4e00-\u9fff]/.test(word);

    if (isChinese) {
      Toast.info("当前功能只针对英文文本哦～");
      return;
    }
    setQueryData({ word });
    setQueryText("");
    getWordInfo(word);
    setQueryVisible(true);
  };

  //收藏

  const handleClickStar = async (isAdd) => {
    if (starLoading) {
      return;
    }
    setStarLoading(true);
    if (isAdd) {
      setQueryData({ ...queryData, isExist: -1 });
      try {
        const res = await wordCollections({
          text: queryData?.word,
          en: queryData?.en,
          uk: queryData?.uk,
          detail: queryText,
          member_id: JSON.parse(localStorage.getItem("userInfo")).member_id,
          token: localStorage.getItem("access_token"),
        });
        if (res.code === 200) {
          setQueryData({ ...queryData, isExist: res.data?.word_id });
        } else {
          setQueryData({ ...queryData, isExist: 0 });
          Toast.fail("收藏失败，请重试...");
        }
      } catch {
        setQueryData({ ...queryData, isExist: 0 });
        Toast.fail("收藏失败，请重试...");
      }
    } else {
      const wordId = queryData?.isExist;
      setQueryData({ ...queryData, isExist: 0 });
      try {
        const res = await unwordCollections({
          word_id: wordId,
          member_id: JSON.parse(localStorage.getItem("userInfo")).member_id,
          token: localStorage.getItem("access_token"),
        });
        if (res.code !== 200) {
          setQueryData({ ...queryData, isExist: wordId });
          Toast.fail("取消收藏失败，请重试...");
        }
      } catch {
        setQueryData({ ...queryData, isExist: wordId });
        Toast.fail("取消收藏失败，请重试...");
      }
    }
    setStarLoading(false);
  };

  const renderAudioMessage = (item) => {
    const audioSrcs = [item.message];
    return (
      <>
        <div className={styles.text}>课堂音频</div>
        <div className={styles.audioList}>
          <img
            className={styles.audioBtn_1}
            src={require("@/images/home/classroom_1.png")}
            onClick={() => handlePlayAudios(audioSrcs)}
          />
        </div>
      </>
    );
  };
  // 聊天区只渲染文本和音频播放按钮
  const renderTextMessage = (item, rowId) => {
    const audios = Array.isArray(item.bin_data)
      ? item.bin_data
      : parseBinData(item.bin_data);
    const audioSrcs = (audios || []).map((audio) =>
      audio.startsWith("http") ? audio : `${cdnDomain}/${audio}`,
    );
    return (
      <>
        <div
          className={`${styles.text} ${
            item.translationShow || item.helpShow || item.optimizeShow
              ? styles["text--hasBorder"]
              : ""
          }`}
        >
          {convertSpan(item.message, handleWordClick, rowId)}{" "}
          {/* 使用 convertSpan 方法 */}
        </div>
        {item.translationShow === true && (
          <div className={styles.anotherText}>
            {item.translationText || "正在加载翻译，请稍后..."}
          </div>
        )}
        {item.helpShow === true && (
          <div className={styles.anotherText}>
            {item.helpText || "正在加载智能助答，请稍后..."}
          </div>
        )}
        {item.optimizeShow === true && (
          <div className={styles.anotherText}>
            {item.optimizeText || "正在加载语句优化，请稍后..."}
          </div>
        )}
        {audioSrcs.length > 0 && (
          <div className={styles.audioList}>
            <img
              className={styles.audioBtn_1}
              src={require("@/images/home/classroom_1.png")}
              onClick={() => handlePlayAudios(audioSrcs)}
            />
            {item.type === "teacher" && (
              <>
                <img
                  className={styles.audioBtn_1}
                  src={require("@/images/home/classroom_2.png")}
                  onClick={() => handleTranslate(item)}
                />
                {(item.prompt === 1 || item.prompt === 7) &&
                  typeof item.message === "string" &&
                  (item.message.includes("？") ||
                    item.message.includes("?")) && (
                    <img
                      className={styles.audioBtn_2}
                      src={require("@/images/home/classroom_3.png")}
                      onClick={() => handleHelp(item)}
                    />
                  )}
              </>
            )}
            {item.type === "user" && (
              <img
                className={styles.audioBtn_2}
                src={require("@/images/home/classroom_4.png")}
                onClick={() => handleOptimize(item)}
              />
            )}
          </div>
        )}
      </>
    );
  };

  // 横竖屏判断逻辑
  useEffect(() => {
    const checkLandscape = () => {
      const ratio = window.innerWidth / window.innerHeight;
      setIsLandscape(ratio > 1.2);
    };
    checkLandscape();
    window.addEventListener("resize", checkLandscape);
    return () => window.removeEventListener("resize", checkLandscape);
  }, []);

  return (
    <div
      className={styles.classroomLesson}
      style={hideTopBar ? { paddingTop: 0 } : {}}
    >
      {isLandscape ? (
        // 横屏布局
        <div className={styles.landscapeContainer}>
          <div className={styles.landscapeSwiperBox}>
            <div className={styles.landscapeSwiperInner}>
              {mediaList.length > 0 && (
                <Swiper
                  onSwiper={(swiper) => (swiperRef.current = swiper)}
                  allowTouchMove={false}
                  slidesPerView={1}
                  spaceBetween={10}
                  initialSlide={0}
                  className={styles.swiper}
                  style={{ width: "100%", height: "100%" }}
                >
                  {mediaList.map((media, idx) => (
                    <SwiperSlide
                      key={media.id ? `${media.id}_${idx}` : idx}
                      className={styles.swiperSlide}
                    >
                      {media.type === "image" ? (
                        <img
                          className={styles.swiperImg}
                          src={media.src}
                          alt="课件图片"
                        />
                      ) : (
                        <div className={styles.swiperVideo}>
                          <CommonVideo
                            src={media.src}
                            poster={
                              media.src
                                ? media.src + "?vframe/jpg/offset/1/w/320/h/180"
                                : ""
                            }
                          />
                        </div>
                      )}
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
              <div className={styles.progressBox}>
                <span>
                  课程进度：{mediaList.length > 0 ? slideIndex + 1 : 0}/
                  {mediaList.length}
                </span>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressInner}
                    style={{
                      width:
                        mediaList.length > 0
                          ? `${((slideIndex + 1) / mediaList.length) * 100}%`
                          : "0%",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.landscapeChatArea}>
            {/* 顶部操作栏 */}
            {!hideTopBar && (
              <div className={styles.topBar}>
                <span className={styles.lessonName}>
                  <span className={styles.dot}></span>Lesson {lessonId}
                </span>
                {/* <span className={styles.speed}>语速: 1.00 &gt;</span> */}
                <span className={styles.exit} onClick={() => navigate(-1)}>
                  退出
                </span>
              </div>
            )}
            <div
              className={styles.chatArea}
              ref={chatAreaRef}
              onScroll={handleChatScroll}
            >
              {loading && (
                <div style={{ textAlign: "center", color: "#888" }}>
                  加载中...
                </div>
              )}
              {!loading && textChatData.length === 0 && (
                <div style={{ textAlign: "center", color: "#888" }}>
                  暂无聊天记录
                </div>
              )}
              {textChatData.map((item, idx) => {
                const isUser = item.type === "user";
                const isTeacher = item.type === "teacher";
                const isAudio = item.type === "audio";
                return (
                  <div
                    key={item.id ? `${item.id}_${idx}` : idx}
                    className={
                      styles.chatItem +
                      " " +
                      (isUser ? styles.user : styles.teacher)
                    }
                    data-id={item.id}
                  >
                    {isUser && (
                      <div className={styles.userContent}>
                        <img
                          className={styles.avatar}
                          src={
                            info?.stu_avatar &&
                            info.stu_avatar.startsWith("http")
                              ? info.stu_avatar
                              : cdnDomain +
                                "/" +
                                info.stu_avatar.replace(/^\/+/, "")
                          }
                          alt="avatar"
                        />
                        <div className={styles.bubble}>
                          {renderTextMessage(item, idx)}
                        </div>
                      </div>
                    )}
                    {isTeacher && (
                      <>
                        <img
                          className={styles.avatar}
                          src={info?.tea_avatar}
                          alt="avatar"
                        />
                        <div className={styles.bubble}>
                          {renderTextMessage(item, idx)}
                        </div>
                      </>
                    )}
                    {isAudio && (
                      <>
                        <img
                          className={styles.avatar}
                          src={info?.tea_avatar}
                          alt="avatar"
                        />
                        <div className={styles.bubble}>
                          {renderAudioMessage(item)}
                        </div>
                      </>
                    )}
                    <span className={styles.time}>
                      {item.created_at?.slice(11, 16)}
                    </span>
                  </div>
                );
              })}
              {/* 底部空白div，便于最后一条消息完全滚入可视区 */}
              <div style={{ height: "400px", flexShrink: 0 }} />
            </div>
            <div className={styles.recordArea}>
              <div className={styles.recordTip}>点击按钮，发送录音</div>
              <button
                className={
                  styles.recordBtn + (recording ? " " + styles.recording : "")
                }
                onMouseDown={() => setRecording(true)}
                onMouseUp={() => setRecording(false)}
              >
                <span className={styles.micIcon}></span>
              </button>
              <div className={styles.recordDesc}>长按说话</div>
            </div>
            <div className={styles.footer}>
              <div className={styles.stars}>
                <span className={styles.score}>{score}</span>
                <span className={styles.scoreText}>分</span>
                {[1, 2, 3, 4, 5].map((i) =>
                  score >= i * 20 ? (
                    <img
                      className={styles.star}
                      key={i}
                      src={require("@/images/home/star.png")}
                    />
                  ) : (
                    <img
                      className={styles.star}
                      key={i}
                      src={require("@/images/home/star_disabled.png")}
                    />
                  ),
                )}
              </div>
              <div className={styles.lessonProgress}>课程进行：{time}</div>
            </div>
            <audio ref={globalAudioRef} style={{ display: "none" }} />
          </div>
        </div>
      ) : (
        // 竖屏布局，原有结构
        <>
          {/* 顶部操作栏 */}
          {!hideTopBar && (
            <div className={styles.topBar}>
              <span className={styles.lessonName}>
                <span className={styles.dot}></span>Lesson {lessonId}
              </span>
              {/* <span className={styles.speed}>语速: 1.00 &gt;</span> */}
              <span className={styles.exit} onClick={() => navigate(-1)}>
                退出
              </span>
            </div>
          )}
          {/* 课件轮播区 */}
          {mediaList.length > 0 && (
            <div className={styles.swiperBox}>
              <Swiper
                onSwiper={(swiper) => (swiperRef.current = swiper)}
                allowTouchMove={false}
                slidesPerView={1}
                spaceBetween={10}
                initialSlide={0}
                className={styles.swiper}
                style={{ width: "100%", height: "100%" }}
              >
                {mediaList.map((media, idx) => (
                  <SwiperSlide
                    key={media.id ? `${media.id}_${idx}` : idx}
                    className={styles.swiperSlide}
                  >
                    {media.type === "image" ? (
                      <img
                        className={styles.swiperImg}
                        src={media.src}
                        alt="课件图片"
                      />
                    ) : (
                      <div className={styles.swiperVideo}>
                        <CommonVideo
                          src={media.src}
                          poster={
                            media.src
                              ? media.src + "?vframe/jpg/offset/1/w/320/h/180"
                              : ""
                          }
                        />
                      </div>
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}
          {/* 课程进度条 */}
          <div className={styles.progressBox}>
            <span>
              课程进度：{mediaList.length > 0 ? slideIndex + 1 : 0}/
              {mediaList.length}
            </span>
            <div className={styles.progressBar}>
              <div
                className={styles.progressInner}
                style={{
                  width:
                    mediaList.length > 0
                      ? `${((slideIndex + 1) / mediaList.length) * 100}%`
                      : "0%",
                }}
              ></div>
            </div>
          </div>
          {/* 聊天区 */}
          <div
            className={styles.chatArea}
            ref={chatAreaRef}
            onScroll={handleChatScroll}
          >
            {loading && (
              <div style={{ textAlign: "center", color: "#888" }}>
                加载中...
              </div>
            )}
            {!loading && textChatData.length === 0 && (
              <div style={{ textAlign: "center", color: "#888" }}>
                暂无聊天记录
              </div>
            )}
            {textChatData.map((item, idx) => {
              const isUser = item.type === "user";
              const isTeacher = item.type === "teacher";
              const isAudio = item.type === "audio";
              return (
                <div
                  key={item.id ? `${item.id}_${idx}` : idx}
                  className={
                    styles.chatItem +
                    " " +
                    (isUser ? styles.user : styles.teacher)
                  }
                  data-id={item.id}
                >
                  {isUser && (
                    <div className={styles.userContent}>
                      <img
                        className={styles.avatar}
                        src={
                          info?.stu_avatar && info.stu_avatar.startsWith("http")
                            ? info.stu_avatar
                            : cdnDomain +
                              "/" +
                              info.stu_avatar.replace(/^\/+/, "")
                        }
                        alt="avatar"
                      />
                      <div className={styles.bubble}>
                        {renderTextMessage(item, idx)}
                      </div>
                    </div>
                  )}
                  {isTeacher && (
                    <>
                      <img
                        className={styles.avatar}
                        src={info?.tea_avatar}
                        alt="avatar"
                      />
                      <div className={styles.bubble}>
                        {renderTextMessage(item, idx)}
                      </div>
                    </>
                  )}
                  {isAudio && (
                    <>
                      <img
                        className={styles.avatar}
                        src={info?.tea_avatar}
                        alt="avatar"
                      />
                      <div className={styles.bubble}>
                        {renderAudioMessage(item)}
                      </div>
                    </>
                  )}
                  <span className={styles.time}>
                    {item.created_at?.slice(11, 16)}
                  </span>
                </div>
              );
            })}
            <div style={{ height: "400px", flexShrink: 0 }} />
          </div>
          {/* 录音按钮区 */}
          <div className={styles.recordArea}>
            <div className={styles.recordTip}>点击按钮，发送录音</div>
            <button
              className={
                styles.recordBtn + (recording ? " " + styles.recording : "")
              }
              onMouseDown={() => setRecording(true)}
              onMouseUp={() => setRecording(false)}
            >
              <span className={styles.micIcon}></span>
            </button>
            <div className={styles.recordDesc}>长按说话</div>
          </div>
          {/* 底部评分与进度 */}
          <div className={styles.footer}>
            <div className={styles.stars}>
              <span className={styles.score}>{score}</span>
              <span className={styles.scoreText}>分</span>
              {[1, 2, 3, 4, 5].map((i) =>
                score >= i * 20 ? (
                  <img
                    className={styles.star}
                    key={i}
                    src={require("@/images/home/star.png")}
                  />
                ) : (
                  <img
                    className={styles.star}
                    key={i}
                    src={require("@/images/home/star_disabled.png")}
                  />
                ),
              )}
            </div>
            <div className={styles.lessonProgress}>课程进行：{time}</div>
          </div>
          <audio ref={globalAudioRef} style={{ display: "none" }} />
        </>
      )}
      {queryVisible && (
        <div
          className={styles.queryContainer}
          onClick={() => {
            setQueryVisible(false);
            setQueryData(null);
            setQueryText("");
            setStarShow(false);
            setHighlightedWord({
              rowId: null,
              index: null,
            });
          }}
        >
          <div
            className={styles["queryBox"]}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className={styles["header"]}>
              <div className={styles["header-left"]}>
                <span>{queryData?.word || ""}</span>
                {starShow &&
                  queryData?.isExist !== undefined &&
                  (queryData?.isExist == 0 ? (
                    <Star
                      color="rgba(0, 0, 0, 0.2)"
                      fontSize="22px"
                      onClick={() => handleClickStar(true)}
                    />
                  ) : (
                    <Star
                      color="rgba(255, 195, 0, 1)"
                      fontSize="22px"
                      onClick={() => handleClickStar(false)}
                    />
                  ))}
              </div>
              <img
                className={styles["header-right"]}
                src={require("@/images/image/recommended-cancel.png")}
                onClick={() => {
                  setQueryVisible(false);
                  setQueryData(null);
                  setQueryText("");
                  setStarShow(false);
                  setHighlightedWord({
                    rowId: null,
                    index: null,
                  });
                }}
              />
            </div>
            {(queryData?.uk || queryData?.uk) && (
              <div className={styles["symbol"]}>
                <div className={styles["symbol-text"]}>
                  {`英/${queryData?.uk || ""}/`}
                </div>
                <img
                  className={styles["symbol-icon"]}
                  src={
                    audioState.srcs[0] === queryData?.ukAudio &&
                    audioState.playing
                      ? require("@/images/image/audio-play.png")
                      : require("@/images/image/audio-unplay.png")
                  }
                  onClick={() => {
                    if (queryData?.ukAudio) {
                      handlePlayAudios([queryData?.ukAudio]);
                    } else {
                      Toast.fail("暂无音频");
                    }
                  }}
                />
                <div className={styles["symbol-text"]}>{`美/${
                  queryData?.en || ""
                }/`}</div>
                <img
                  className={styles["symbol-icon"]}
                  src={
                    audioState.srcs[0] === queryData?.enAudio &&
                    audioState.playing
                      ? require("@/images/image/audio-play.png")
                      : require("@/images/image/audio-unplay.png")
                  }
                  onClick={() => {
                    if (queryData?.enAudio) {
                      handlePlayAudios([queryData?.enAudio]);
                    } else {
                      Toast.fail("暂无音频");
                    }
                  }}
                />
              </div>
            )}
            <div className={styles["content"]}>
              <div className={styles["explain"]}>{queryText}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassroomLesson;
